import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ordersApi, transportApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'

/**
 * The order's transport service and its reference (LR / docket) number.
 *
 * The customer usually picks the service on their order page; the shop adds
 * the reference once the consignment is booked, which can be well after the
 * order was placed — so this stays editable at every status.
 */
// `needsReference`: the order is ready to dispatch and has no reference yet,
// which dispatch requires. The panel then opens on the editor, with the
// Reference No. box focused, rather than waiting for someone to find 'Add'.
export default function TransportPanel({ order, editable, needsReference = false }) {
  const queryClient = useQueryClient()
  const openOnLoad = editable && needsReference
  const [editing, setEditing] = useState(openOnLoad)
  const [serviceId, setServiceId] = useState(() =>
    openOnLoad && order.transport ? String(order.transport.id) : '',
  )
  const [reference, setReference] = useState(() =>
    openOnLoad ? order.transport_reference ?? '' : '',
  )
  const [error, setError] = useState('')
  const transport = order.transport
  const city = order.customer?.city

  // Active services, the customer's city first: that is where theirs runs
  // from, and a shop serving one district would otherwise scroll past every
  // other town to find it.
  // Only what may go on this customer's order: the shop's own services and
  // the ones this customer added — never another customer's, even in the
  // same city or awaiting approval.
  const { data: services } = useQuery({
    queryKey: ['transport-services', { is_active: true, for_customer: order.customer_id }],
    queryFn: () => transportApi.list({ is_active: true, for_customer: order.customer_id }),
    enabled: editing,
  })
  const sorted = [...(services ?? [])].sort((a, b) => {
    const local = (s) => (city && s.city.toLowerCase() === city.toLowerCase() ? 0 : 1)
    return local(a) - local(b) || a.city.localeCompare(b.city) || a.name.localeCompare(b.name)
  })
  // The current choice stays offered even if it has since been switched off.
  const listed =
    transport && !sorted.some((s) => s.id === transport.id) ? [transport, ...sorted] : sorted

  const save = useMutation({
    mutationFn: () =>
      ordersApi.setTransport(order.id, {
        transport_service_id: serviceId ? Number(serviceId) : null,
        transport_reference: reference,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['order', String(order.id)], updated)
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['transport-services'] })
      setEditing(false)
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const start = () => {
    setServiceId(transport ? String(transport.id) : '')
    setReference(order.transport_reference ?? '')
    setError('')
    setEditing(true)
  }

  // The order can become ready to dispatch while this page is open — moved
  // to Packed a moment ago. Open the editor then too.
  useEffect(() => {
    if (editable && needsReference && !editing) start()
  }, [editable, needsReference]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="transport-panel">
      <div className="panel-title panel-title-row">
        Transport
        {editable && !editing && (
          <button type="button" className="btn btn-sm" onClick={start}>
            {transport || order.transport_reference ? 'Edit' : 'Add'}
          </button>
        )}
      </div>

      {!editing && (
        <dl className="kv">
          <dt>Service</dt>
          <dd>
            {transport ? (
              <>
                {transport.name}
                {!transport.is_approved && (
                  <div>
                    <span className="tag-pending">Awaiting approval</span>
                  </div>
                )}
              </>
            ) : (
              <span className="muted">Not chosen</span>
            )}
          </dd>
          {transport && (
            <>
              <dt>Contact</dt>
              <dd>
                {[transport.contact_number, transport.contact_number_2, transport.contact_number_3]
                  .filter(Boolean)
                  .map((number) => (
                    <div key={number}>
                      <a href={`tel:${number}`}>{number}</a>
                    </div>
                  ))}
              </dd>
              <dt>City</dt>
              <dd>{[transport.city, transport.state].filter(Boolean).join(', ')}</dd>
              {transport.address && (
                <>
                  <dt>Address</dt>
                  <dd className="pre-line">{transport.address}</dd>
                </>
              )}
            </>
          )}
          <dt>Reference No.</dt>
          <dd className={order.transport_reference ? 'mono' : undefined}>
            {order.transport_reference || <span className="muted">Not added yet</span>}
          </dd>
        </dl>
      )}

      {editing && (
        <form
          className="transport-edit"
          onSubmit={(e) => {
            e.preventDefault()
            setError('')
            save.mutate()
          }}
        >
          <label className="field">
            <span>Transport service</span>
            <select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
              <option value="">{services ? 'None' : 'Loading…'}</option>
              {listed.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.city}
                  {s.is_approved === false ? ' (awaiting approval)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Reference No. (LR / docket)</span>
            <input
              value={reference}
              autoFocus={needsReference}
              maxLength={64}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. LR 45218"
            />
          </label>
          <p className="hint">
            {['dispatched', 'delivered'].includes(order.status)
              ? 'A new reference number is sent to the customer on WhatsApp when you save.'
              : 'Sent to the customer with the dispatch message. Dispatch needs it.'}
          </p>
          {error && <p className="field-error">{error}</p>}
          <div className="transport-edit-actions">
            <button type="button" className="btn btn-sm" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
